import { Injectable } from '@angular/core';
import { TableParams } from '@core/models';
import { ApiDataService } from '@core/services/api/api-data.service';
import { ApiClientService } from '@core/services/client/api-client.service';
import { AvailableFilters, FilterTypeRanges } from '@shared/services/filters/types';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';

interface CategoryFilterCacheEntry {
  value: any;
  timestamp: number;
}

@Injectable({providedIn: 'root'})
export class ApiInventoryService extends ApiDataService {
  apiUrl = 'client/inventory_works';
  apiUrlFilters = 'client/inventory_filters';
  private readonly categoryFilterCache = new Map<string, CategoryFilterCacheEntry>();
  private readonly pendingCategoryFilterRequests = new Map<string, Observable<any>>();
  private readonly categoryFilterCacheLimit = 100;
  private readonly categoryFilterCacheTtl = 5 * 60 * 1000;

  buyIsAvailable(id: number): Promise<boolean> {
    const url = this.apiUrlService.getApiUrl() + this.apiUrl;
    return this.httpClient.get<{ result: boolean }>(`${url}/buy-is-available/${id}`)
      .toPromise()
      .then((value) => value.result)
      .catch(error => this.handleError(error));
  }

  getInventoryFilters(): Observable<AvailableFilters> {
    const params = new URLSearchParams();
    // Pamięć konfiguracji trzyma ostatnią znaną wartość i nie jest czyszczona przy wygaśnięciu
    // TTL, ale przed pierwszym pobraniem bywa pusta — czytamy ją odpornie zamiast zakładać wpis.
    params.set('client_id', String(ApiClientService.CACHED_CLIENT?.id ?? ''));
    const url = this.apiUrlService.getApiUrl() + this.apiUrlFilters;
    return this.httpClient.get<any>(this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${url}?${params.toString()}`)).pipe(
      map(filters => {
        const returnObj: unknown = {};
        Object.keys(filters.data).forEach(key => {
          if([
            FilterTypeRanges.PRICE,
            FilterTypeRanges.YEAR
          ].includes(key as FilterTypeRanges)){
            returnObj[key] = filters.data[key];
            return;
          }
          returnObj[key] = filters.data[key].map(item => ({
            ...item,
            parentId: item.parentId || null,
            display: true,
            count: 0,
            disabled: false,
          }));
        });
        return returnObj as AvailableFilters;
      }),
    );
  }

  getListCategoryFilter(data: TableParams): Observable<any> {
    const url = this.apiUrlService.getApiUrl() + this.apiUrl + '/list-category-filter';
    const cacheKey = this.getListCategoryFilterCacheKey(url, data);
    const cached = this.getCategoryFilterCacheValue(cacheKey);

    if (cached) {
      return of(structuredClone(cached));
    }

    const pendingRequest = this.pendingCategoryFilterRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest.pipe(
        map(result => structuredClone(result)),
      );
    }

    const request = this.httpClient.get<any>(this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(url + data.getUrlParams())).pipe(
      tap(result => this.setCategoryFilterCacheValue(cacheKey, result)),
      finalize(() => this.pendingCategoryFilterRequests.delete(cacheKey)),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.pendingCategoryFilterRequests.set(cacheKey, request);

    return request.pipe(
      map(result => structuredClone(result)),
    );
  }

  /**
   * Pobiera kompletny indeks danych do lokalnego facetingu. Zwraca pełny
   * zestaw prac klienta z ich atrybutami plus słowniki nazw — dzięki temu
   * kolejne liczniki filtrów są liczone w przeglądarce, bez requestu do
   * backendu. Backend cache'uje odpowiedź 5 min.
   */
  getFacetIndex(lang: string): Observable<any> {
    const url = this.apiUrlService.getApiUrl() + this.apiUrl + '/facet-index';
    const params = new URLSearchParams();
    params.set('lang', lang);
    params.set('clientId', String(ApiClientService.CACHED_CLIENT?.id ?? ''));
    return this.httpClient.get<any>(this.currencyPreferenceService.appendCurrencyToUrlIfSwitcherExists(`${url}?${params.toString()}`));
  }

  private handleError(error: any) {
    return error;
  }

  private getListCategoryFilterCacheKey(url: string, data: TableParams): string {
    const params = data.toPlainObject();
    const normalizedParams = Object.keys(params).sort().reduce((result, key) => {
      result[key] = this.normalizeCacheValue(params[key]);
      return result;
    }, {});

    return JSON.stringify({
      url,
      currency: this.currencyPreferenceService.getCurrentCurrency(),
      clientId: ApiClientService.CACHED_CLIENT?.id,
      params: normalizedParams,
    });
  }

  private normalizeCacheValue(value: any): any {
    if (Array.isArray(value)) {
      return [...value].map(item => this.normalizeCacheValue(item)).sort();
    }

    return value;
  }

  private getCategoryFilterCacheValue(cacheKey: string): any | null {
    const entry = this.categoryFilterCache.get(cacheKey);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > this.categoryFilterCacheTtl) {
      this.categoryFilterCache.delete(cacheKey);
      return null;
    }

    return entry.value;
  }

  private setCategoryFilterCacheValue(cacheKey: string, value: any): void {
    if (this.categoryFilterCache.has(cacheKey)) {
      this.categoryFilterCache.delete(cacheKey);
    }

    this.categoryFilterCache.set(cacheKey, {
      value: structuredClone(value),
      timestamp: Date.now(),
    });

    while (this.categoryFilterCache.size > this.categoryFilterCacheLimit) {
      const oldestCacheKey = this.categoryFilterCache.keys().next().value;
      this.categoryFilterCache.delete(oldestCacheKey);
    }
  }
}
